export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function valueToFixedNumber(value: number, fixed: number) {
  return value.toFixed(fixed);
}

const MILLISECONDS_PER_SECOND = 1_000;
const MILLISECONDS_PER_MINUTE = 60_000;
const MILLISECONDS_PER_HOUR = 3_600_000;
const MILLISECONDS_PER_DAY = 86_400_000;

/**
 * Display duration as "hh:mm:ss", or "Xd hh:mm:ss" when the value spans at least one day.
 * Whole seconds only. Use {@link formatTimeSpan} for a .NET TimeSpan API payload.
 */
export function formatDuration(milliseconds: number) {
  let seconds = Math.floor(milliseconds / 1000);

  const days = Math.floor(seconds / (3600 * 24));
  seconds = seconds % (3600 * 24);

  const hours = Math.floor(seconds / 3600);
  seconds = seconds % 3600;

  const minutes = Math.floor(seconds / 60);
  seconds = seconds % 60;

  const formattedTime = `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
  if (days > 0) {
    return `${days}d ${formattedTime}`;
  }

  return formattedTime;
}

/**
 * Formats a non-negative whole-millisecond duration as a .NET TimeSpan string:
 * `[d.]hh:mm:ss[.fff]`. Distinct from {@link formatDuration}, which is display text.
 */
export function formatTimeSpan(totalMilliseconds: number): string {
  if (!Number.isSafeInteger(totalMilliseconds) || totalMilliseconds < 0) {
    throw new Error("A duration must be a non-negative whole number of milliseconds.");
  }

  const days = Math.floor(totalMilliseconds / MILLISECONDS_PER_DAY);
  const hours = Math.floor((totalMilliseconds % MILLISECONDS_PER_DAY) / MILLISECONDS_PER_HOUR);
  const minutes = Math.floor((totalMilliseconds % MILLISECONDS_PER_HOUR) / MILLISECONDS_PER_MINUTE);
  const seconds = Math.floor((totalMilliseconds % MILLISECONDS_PER_MINUTE) / MILLISECONDS_PER_SECOND);
  const milliseconds = totalMilliseconds % MILLISECONDS_PER_SECOND;
  const dayPrefix = days > 0 ? `${days}.` : "";
  const millisecondSuffix = milliseconds > 0 ? `.${milliseconds.toString().padStart(3, "0")}` : "";
  const time = [hours, minutes, seconds].map(padZero).join(":");

  return `${dayPrefix}${time}${millisecondSuffix}`;
}

function padZero(num: number) {
  return num.toString().padStart(2, "0");
}
